import type { FindriscAnswers, FindriscResults } from '../types/findrisc';
import { LOINC_FINDRISC_QUESTIONNAIRE } from './loinc-questionnaire';

export const generateFhirQuestionnaire = () => {
  return LOINC_FINDRISC_QUESTIONNAIRE;
};

export const generateFhirQuestionnaireResponse = (answers: FindriscAnswers, results: FindriscResults) => {
  // Map BMI to LOINC LA code
  const getBmiLaCode = (bmi: number) => {
    if (bmi < 25) return 'LA31984-0'; // Lower than 25 kg/m^2
    if (bmi < 30) return 'LA31985-7'; // 25-30 kg/m^2
    return 'LA31986-5'; // Higher than 30 kg/m^2
  };

  const getBmiDisplay = (bmi: number) => {
    if (bmi < 25) return 'Lower than 25 kg/m^2';
    if (bmi < 30) return '25-30 kg/m^2';
    return 'Higher than 30 kg/m^2';
  };

  return {
    resourceType: 'QuestionnaireResponse',
    id: 'findrisc-response-' + Date.now(),
    questionnaire: import.meta.env.VITE_QUESTIONNAIRE_ID,
    status: 'completed',
    authored: new Date().toISOString(),
    item: [
      ...(answers.age ? [{
        linkId: '124299',
        text: 'Age',
        answer: [{
          valueCoding: {
            system: 'http://loinc.org',
            code: answers.age,
            display: answers.age === 'LA31987-3' ? 'Under 45 years' :
                     answers.age === 'LA31988-1' ? '45-54 years' :
                     answers.age === 'LA31989-9' ? '55-64 years' : 'Over 64 years'
          }
        }]
      }] : []),
      ...(answers.bmi ? [{
        linkId: '124240',
        text: 'Body mass index',
        answer: [{
          valueCoding: {
            system: 'http://loinc.org',
            code: getBmiLaCode(answers.bmi),
            display: getBmiDisplay(answers.bmi)
          }
        }]
      }] : []),
      ...(answers.waistCircumference ? [{
        linkId: '124239',
        text: 'Waist circumference measured below the ribs (usually at the level of the navel)',
        answer: [{
          valueCoding: {
            system: 'http://loinc.org',
            code: answers.waistCircumference,
            display: answers.waistCircumference === 'LA31920-4' ? 'Men: less than 94 cm' :
                     answers.waistCircumference === 'LA31921-2' ? 'Men: 94-102 cm' :
                     answers.waistCircumference === 'LA31922-0' ? 'Men: more than 102 cm' :
                     answers.waistCircumference === 'LA31923-8' ? 'Women: less than 80 cm' :
                     answers.waistCircumference === 'LA31924-6' ? 'Women: 80-88 cm' : 'Women: more than 88 cm'
          }
        }]
      }] : []),
      ...(answers.physicalActivity ? [{
        linkId: '124238',
        text: 'Do you usually have daily at least 30 min of physical activity at work and/or during leisure time (including normal daily activity)?',
        answer: [{
          valueCoding: {
            system: 'http://loinc.org',
            code: answers.physicalActivity,
            display: answers.physicalActivity === 'LA33-6' ? 'Yes' : 'No'
          }
        }]
      }] : []),
      ...(answers.vegetablesFruits ? [{
        linkId: '124237',
        text: 'How often do you eat vegetables, fruit, or berries',
        answer: [{
          valueCoding: {
            system: 'http://loinc.org',
            code: answers.vegetablesFruits,
            display: answers.vegetablesFruits === 'LA14799-3' ? 'Every day' : 'Not every day'
          }
        }]
      }] : []),
      ...(answers.bloodPressureMeds ? [{
        linkId: '124301',
        text: 'Have you ever taken anti-hypertensive medication regularly?',
        answer: [{
          valueCoding: {
            system: 'http://loinc.org',
            code: answers.bloodPressureMeds,
            display: answers.bloodPressureMeds === 'LA33-6' ? 'Yes' : 'No'
          }
        }]
      }] : []),
      ...(answers.highBloodGlucose ? [{
        linkId: '124236',
        text: 'Have you ever been found to have high blood glucose (e.g. in a health examination, during an illness, during pregnancy)?',
        answer: [{
          valueCoding: {
            system: 'http://loinc.org',
            code: answers.highBloodGlucose,
            display: answers.highBloodGlucose === 'LA33-6' ? 'Yes' : 'No'
          }
        }]
      }] : []),
      ...(answers.familyHistory ? [{
        linkId: '124235',
        text: 'Have any of the members of your immediate family or other relatives been diagnosed with diabetes (type 1 or type 2)?',
        answer: [{
          valueCoding: {
            system: 'http://loinc.org',
            code: answers.familyHistory,
            display: answers.familyHistory === 'LA32-8' ? 'No' :
                     answers.familyHistory === 'LA31927-9' ? 'Yes: grandparent, aunt, uncle, or first cousin (but not own parent, brother, sister or child)' :
                     'Yes: parent, brother, sister, or own child'
          }
        }]
      }] : []),
      {
        linkId: '124251',
        text: 'Total risk score FINDRISC',
        answer: [{
          valueDecimal: results.totalScore
        }]
      }
    ]
  };
};